import { DEFAULT_LLM } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { getMaxPrompts } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type {
    ExplicitLLModel,
    ILanguage,
    IRawCompetitor,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { ICountryObject } from "app/services/CountryService";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

export const STEPS_COUNT = 3;
export const MIN_PROMPTS_PER_TOPIC = 1;
export const DEFAULT_PROMPTS_PER_TOPIC = 0;
export const INITIAL_PROMPTS_WITH_TOPICS = 30;
export const MAX_COMPETITORS = 20;
export const LLM_SETUP_MODAL_HEIGHT = 600;
export const INVALID_TOPIC_CHARACTERS_REGEX = /[“”„‟"＂]/;
export const DEFAULT_LANGUAGE: ILanguage = {
    id: "en",
    name: "English",
};

export interface ILocaleState {
    countryId: number;
    langId: string;
}

interface State {
    domains: IWebsiteItem[];
    brandNames: string[];
    suggestedTopics: string[];
    selectedTopics: string[];
    promptsPerTopic: number;
    competitors: IRawCompetitor[];
    currentStep: number;
    selectedCountry: ICountryObject;
    selectedLanguage: ILanguage;
    selectedModels: ExplicitLLModel[];
    includeSubdomains: boolean;
    shouldGenerateCompetitors: boolean;
}

type Action =
    | { type: "SET_DOMAINS"; payload: IWebsiteItem[] }
    | { type: "SET_BRAND_NAMES"; payload: string[] }
    | { type: "SET_SUGGESTED_TOPICS"; payload: string[] }
    | { type: "SET_SELECTED_TOPICS"; payload: string[] }
    | { type: "SET_PROMPTS_PER_TOPIC"; payload: number }
    | { type: "SET_MANUAL_COMPETITORS"; payload: IRawCompetitor[] }
    | { type: "SET_AUTOMATIC_COMPETITORS"; payload: IRawCompetitor[] }
    | { type: "SET_STEP"; payload: number }
    | { type: "ADD_TOPIC"; payload: string }
    | { type: "REMOVE_TOPIC"; payload: string }
    | { type: "SET_SELECTED_COUNTRIES"; payload: ICountryObject }
    | { type: "SET_SELECTED_LANGUAGE"; payload: ILanguage }
    | { type: "SET_INCLUDE_SUBDOMAINS"; payload: boolean }
    | { type: "SET_SELECTED_MODELS"; payload: ExplicitLLModel[] }
    | {
          type: "INITIALIZE_CAMPAIGN";
          payload: {
              suggestedTopics: string[];
              selectedCountry: ICountryObject;
              selectedLanguage: ILanguage;
          };
      }
    | {
          type: "UPDATE_TOPICS_DATA";
          payload: {
              suggestedTopics: string[];
          };
      }
    | { type: "CLEAR_ALL" };

export const EMPTY_COMPETITOR: IRawCompetitor = { domain: null, brands: [] };

export const initialState: State = {
    domains: [],
    brandNames: [],
    suggestedTopics: [],
    selectedTopics: [],
    promptsPerTopic: DEFAULT_PROMPTS_PER_TOPIC,
    competitors: [EMPTY_COMPETITOR],
    currentStep: 1,
    selectedCountry: { id: 840, text: "United States", code: "US" }, // Default to United States
    selectedLanguage: DEFAULT_LANGUAGE,
    selectedModels: [DEFAULT_LLM],
    shouldGenerateCompetitors: true,
    includeSubdomains: false,
};

export function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "SET_DOMAINS":
            return { ...state, domains: action.payload };
        case "SET_BRAND_NAMES":
            return { ...state, brandNames: action.payload };
        case "SET_SUGGESTED_TOPICS": {
            // Remove duplicates while preserving case
            const uniqueTopics = Array.from(new Set(action.payload));
            return { ...state, suggestedTopics: uniqueTopics };
        }
        case "SET_SELECTED_TOPICS": {
            // Remove duplicates while preserving case
            const uniqueTopics = Array.from(new Set(action.payload));
            return { ...state, selectedTopics: uniqueTopics };
        }
        case "SET_PROMPTS_PER_TOPIC": {
            const maxAvailableTopics = getMaxPrompts();
            const clampedValue = Math.floor(
                Math.max(MIN_PROMPTS_PER_TOPIC, Math.min(action.payload, maxAvailableTopics)),
            );

            return { ...state, promptsPerTopic: clampedValue };
        }
        case "SET_MANUAL_COMPETITORS": {
            return { ...state, competitors: action.payload, shouldGenerateCompetitors: false };
        }
        case "SET_AUTOMATIC_COMPETITORS": {
            return { ...state, competitors: action.payload, shouldGenerateCompetitors: false };
        }
        case "SET_STEP": {
            const nextStep = action.payload;

            if (nextStep >= 1 && nextStep <= STEPS_COUNT) {
                return { ...state, currentStep: action.payload };
            }

            return state;
        }
        case "ADD_TOPIC": {
            const newTopic = action.payload;
            if (
                state.selectedTopics.length >= getMaxPrompts() ||
                state.selectedTopics.some((topic) => topic.toLowerCase() === newTopic.toLowerCase())
            ) {
                return state;
            }
            return { ...state, selectedTopics: [...state.selectedTopics, newTopic] };
        }
        case "REMOVE_TOPIC": {
            return {
                ...state,
                selectedTopics: state.selectedTopics.filter((topic) => topic !== action.payload),
            };
        }
        case "SET_SELECTED_COUNTRIES":
            return { ...state, selectedCountry: action.payload };
        case "SET_SELECTED_LANGUAGE":
            return { ...state, selectedLanguage: action.payload };
        case "SET_SELECTED_MODELS":
            return { ...state, selectedModels: action.payload };
        case "SET_INCLUDE_SUBDOMAINS":
            return { ...state, includeSubdomains: action.payload };
        case "INITIALIZE_CAMPAIGN": {
            const uniqueTopics = Array.from(new Set(action.payload.suggestedTopics));
            return {
                ...state,
                suggestedTopics: uniqueTopics,
                selectedCountry: action.payload.selectedCountry,
                selectedLanguage: action.payload.selectedLanguage,
            };
        }
        case "UPDATE_TOPICS_DATA": {
            const uniqueTopics = Array.from(new Set(action.payload.suggestedTopics));
            return {
                ...state,
                suggestedTopics: uniqueTopics,
            };
        }
        case "CLEAR_ALL":
            return initialState;
        default:
            return state;
    }
}
